import { Duration, Timestamp } from '@bufbuild/protobuf';
import { generateTypes } from '@evmos/eip712';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { fromDuration, toTimestamp } from 'cosmjs-types/helpers';
import { MsgCreateGauge } from '../ts-client/incentives/tx';
import { MsgCreateGauge as MsgCreateGaugeProto } from '../proto/tx_pb';
import { QueryCondition as QueryConditionProto } from '../../lockup/proto/lock_pb';
import { Coin } from '../../lockup/proto/coin_pb';

const CREATE_MSG_CREATE_GAUGE_TYPES = (params: MsgCreateGauge) => ({
  MsgValue: [
    { name: 'owner', type: 'string' },
    ...(params.distributeTo ? [ { name: 'distribute_to', type: 'TypeDistributeTo' } ] : []),
    { name: 'coins', type: 'TypeCoins[]' },
    ...(params.startTime ? [ { name: 'start_time', type: 'string' } ] : []),
    { name: 'num_epochs_paid_over', type: 'uint64' },
  ],
  TypeDistributeTo: [
    ...(params.distributeTo?.lockQueryType ? [ { name: 'lock_query_type', type: 'int32' } ] : []),
    { name: 'denom', type: 'string' },
    ...(params.distributeTo?.duration ? [ { name: 'duration', type: 'int64' } ] : []),
    { name: 'timestamp', type: 'string' },
  ],
  TypeCoins: [
    { name: 'denom', type: 'string' },
    { name: 'amount', type: 'string' },
  ],
});

const EMPTY_DATE = new Date('0001-01-01T00:00:00Z');

export const createTxMsgCreateGauge = (context: TxContext, params: MsgCreateGauge): TxPayload => {
  const typedData = createEIP712MsgCreateGauge(context, params);
  const cosmosMsg = createCosmosMsgCreateGauge(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgCreateGauge = (context: TxContext, params: MsgCreateGauge): EIP712TypedData => {
  const types = generateTypes(CREATE_MSG_CREATE_GAUGE_TYPES(params));
  const message = createMsgCreateGauge(params);
  return { types, message };
};

const getFixedStartTime = (params: MsgCreateGauge): string => {
  return (params.startTime || EMPTY_DATE).toISOString().replace('.000Z', 'Z');
};

const getFixedIsoDate = (date: Date): string => {
  return date.toISOString().replace('.000Z', 'Z');
};

const createCosmosMsgCreateGauge = (context: TxContext, params: MsgCreateGauge): any => {
  const startTime = toTimestamp(params.startTime || EMPTY_DATE);
  const message = new MsgCreateGaugeProto({
    isPerpetual: params.isPerpetual,
    owner: params.owner,
    distributeTo: !params.distributeTo ? undefined : new QueryConditionProto({
      lockQueryType: params.distributeTo.lockQueryType,
      denom: params.distributeTo.denom,
      duration: params.distributeTo.duration,
      timestamp: startTime,
    }),
    coins: params.coins.map((coin) => new Coin({ ...coin })),
    startTime,
    numEpochsPaidOver: params.numEpochsPaidOver,
  });
  return { message, path: MsgCreateGaugeProto.typeName };
};

export function createMsgCreateGauge(params: MsgCreateGauge): object | object[] {
  const start_time = getFixedIsoDate(params.startTime || EMPTY_DATE);

  return {
    type: 'dymensionxyz/dymension/incentives/CreateGauge',
    value: {
      owner: params.owner,
      distribute_to: !params.distributeTo ? undefined : {
        denom: params.distributeTo.denom,
        duration: !params.distributeTo?.duration ? undefined : fromDuration(params.distributeTo.duration),
        timestamp: start_time,
        ...(params.distributeTo.lockQueryType && { lock_query_type: params.distributeTo.lockQueryType }),
      },
      coins: params.coins,
      start_time,
      num_epochs_paid_over: params.numEpochsPaidOver,
    },
  };
}

